import React from "react";
import { MONTH_OPTIONS } from "../constants/defaults";
import { isValidCurrencyInput } from "../utils/numericInput";

const MIN_COURSE_FINISH_YEAR = 2009;
const MIN_COURSE_FINISH_MONTH = "01";
const MAX_COURSE_FINISH_YEAR = 2026;
const MAX_COURSE_FINISH_MONTH = "12";

const preventWheelNumberInputChange = (event) => {
  event.currentTarget.blur();
};

const preventNegativeSignInput = (event) => {
  if (event.key === "-" || event.key === "Subtract") {
    event.preventDefault();
  }
};

const handleInfoButtonInteraction = (event) => {
  // Prevent label default behavior so tapping the info icon does not toggle the input.
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.focus();
};

function PlanInputsSection({
  selectedPlans,
  selectedPlanLabels,
  planDetails,
  overpaymentExpanded,
  onPlanDetailChange,
  onToggleOverpayment,
}) {
  const yearOptions = [];

  for (let year = MIN_COURSE_FINISH_YEAR; year <= MAX_COURSE_FINISH_YEAR; year += 1) {
    yearOptions.push(year.toString());
  }

  if (yearOptions.length === 0) {
    yearOptions.push(MAX_COURSE_FINISH_YEAR.toString());
  }

  return selectedPlans.map((planKey) => (
    <div key={planKey} className="plan-details-container">
      <div className="input-group plan-graduation-group">
        <label className="input-label">{selectedPlanLabels[planKey]} Course Finish Date</label>
        <div className="date-select-row">
          <select
            className="styled-dropdown"
            value={planDetails[planKey]?.gradMonth || ""}
            onChange={(e) => {
              const month = e.target.value;
              const year = planDetails[planKey]?.gradYear || "";

              if (year === MIN_COURSE_FINISH_YEAR.toString() && month < MIN_COURSE_FINISH_MONTH) {
                return;
              }

              if (year === MAX_COURSE_FINISH_YEAR.toString() && month > MAX_COURSE_FINISH_MONTH) {
                return;
              }

              onPlanDetailChange(planKey, "gradMonth", month);
              if (year && month) {
                onPlanDetailChange(planKey, "graduationDate", `${year}-${month}-01`);
              }
            }}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.filter((month) => (
              (planDetails[planKey]?.gradYear !== MIN_COURSE_FINISH_YEAR.toString()
                || month.value >= MIN_COURSE_FINISH_MONTH)
              && (planDetails[planKey]?.gradYear !== MAX_COURSE_FINISH_YEAR.toString()
                || month.value <= MAX_COURSE_FINISH_MONTH)
            )).map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            className="styled-dropdown"
            value={planDetails[planKey]?.gradYear || ""}
            onChange={(e) => {
              const year = e.target.value;
              const month = planDetails[planKey]?.gradMonth || "";
              onPlanDetailChange(planKey, "gradYear", year);

              if (year === MIN_COURSE_FINISH_YEAR.toString() && month < MIN_COURSE_FINISH_MONTH) {
                onPlanDetailChange(planKey, "gradMonth", "");
                onPlanDetailChange(planKey, "graduationDate", "");
                return;
              }

              if (year === MAX_COURSE_FINISH_YEAR.toString() && month > MAX_COURSE_FINISH_MONTH) {
                onPlanDetailChange(planKey, "gradMonth", "");
                onPlanDetailChange(planKey, "graduationDate", "");
                return;
              }

              if (year && month) {
                onPlanDetailChange(planKey, "graduationDate", `${year}-${month}-01`);
              }
            }}
          >
            <option value="">Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="input-group">
        <label className="input-label">
          {selectedPlanLabels[planKey]} Current Loan Balance (£)
          <span className="info-btn" tabIndex={0} aria-label="How to check your current loan balance">
            <span className="info-icon">i</span>
            <span className="info-tooltip">
              Check your loan balance.{" "}
              <a
                href="https://www.gov.uk/sign-in-to-manage-your-student-loan-balance"
                target="_blank"
                rel="noopener noreferrer"
              >
                Manage your student loan balance - GOV.UK
              </a>
            </span>
          </span>
        </label>
        <input
          className="styled-input"
          type="text"
          inputMode="decimal"
          step="0.01"
          min="0"
          maxLength={11}
          value={planDetails[planKey]?.balance || ""}
          onChange={(e) => {
            const balance = e.target.value;
            if (!isValidCurrencyInput(balance, { maxLength: 11 })) {
              return;
            }
            onPlanDetailChange(planKey, "balance", balance);
          }}
          onKeyDown={preventNegativeSignInput}
          onWheel={preventWheelNumberInputChange}
        />
      </div>

      <h2
        className="input-label overpayment-heading"
        onClick={() => onToggleOverpayment(planKey)}
      >
        {selectedPlanLabels[planKey]} Overpayment Options
        {` ${overpaymentExpanded[planKey] ? "▲" : "▼"}`}
      </h2>

      {overpaymentExpanded[planKey] && (
        <>
          <div className="input-group input-group-spaced">
            <label className="input-label">Overpayment Type</label>
            <div className="overpayment-type-options">
              <label className="overpayment-radio-label">
                <input
                  type="radio"
                  name={`overpayment_type_${planKey}`}
                  value="lump_sum"
                  checked={planDetails[planKey]?.overpayment_type === "lump_sum"}
                  onChange={(e) => {
                    onPlanDetailChange(planKey, "overpayment_type", e.target.value);
                    onPlanDetailChange(planKey, "overpayment_lump_sum_whole_thing", false);
                  }}
                />
                One-Off Lump Sum (Applied Now)
                <span
                  className="info-btn"
                  tabIndex={0}
                  aria-label="How one-off lump sum timing works"
                  onClick={handleInfoButtonInteraction}
                  onTouchStart={handleInfoButtonInteraction}
                >
                  <span className="info-icon">i</span>
                  <span className="info-tooltip">
                    Applied at the end of the first month only, after interest is added, alongside any scheduled monthly repayment.
                  </span>
                </span>
              </label>
              <label className="overpayment-radio-label">
                <input
                  type="radio"
                  name={`overpayment_type_${planKey}`}
                  value="monthly"
                  checked={planDetails[planKey]?.overpayment_type === "monthly"}
                  onChange={(e) => {
                    onPlanDetailChange(planKey, "overpayment_type", e.target.value);
                    onPlanDetailChange(planKey, "overpayment_lump_sum_whole_thing", false);
                  }}
                />
                Regular Monthly Amount
                <span
                  className="info-btn"
                  tabIndex={0}
                  aria-label="How regular monthly overpayment timing works"
                  onClick={handleInfoButtonInteraction}
                  onTouchStart={handleInfoButtonInteraction}
                >
                  <span className="info-icon">i</span>
                  <span className="info-tooltip">
                    Applied at the end of each month after interest is added, alongside the scheduled monthly repayment.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="input-group">
            <label
              className={
                `input-label overpayment-amount-label${
                  planDetails[planKey]?.overpayment_lump_sum_whole_thing ? " overpayment-amount-label-disabled" : ""
                }`
              }
            >
              Overpayment Amount (£)
            </label>
            <input
              className={
                `styled-input${planDetails[planKey]?.overpayment_lump_sum_whole_thing ? " styled-input-disabled" : ""}`
              }
              type="text"
              inputMode="decimal"
              step="0.01"
              min="0"
              maxLength={11}
              disabled={Boolean(planDetails[planKey]?.overpayment_lump_sum_whole_thing)}
              value={
                planDetails[planKey]?.overpayment_lump_sum_whole_thing
                  ? ""
                  : planDetails[planKey]?.overpayment_amount !== undefined
                  ? planDetails[planKey].overpayment_amount
                  : ""
              }
              onChange={(e) => {
                const amount = e.target.value;
                if (!isValidCurrencyInput(amount, { maxLength: 11 })) {
                  return;
                }
                onPlanDetailChange(planKey, "overpayment_amount", amount);
              }}
              onKeyDown={preventNegativeSignInput}
              onWheel={preventWheelNumberInputChange}
            />
            {planDetails[planKey]?.overpayment_type === "lump_sum" ? (
              <label
                className={
                  `overpayment-whole-loan-checkbox${
                    planDetails[planKey]?.overpayment_lump_sum_whole_thing
                      ? " overpayment-whole-loan-checkbox-selected"
                      : ""
                  }`
                }
              >
                <input
                  type="checkbox"
                  checked={Boolean(planDetails[planKey]?.overpayment_lump_sum_whole_thing)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onPlanDetailChange(planKey, "overpayment_lump_sum_whole_thing", checked);
                    if (checked) {
                      onPlanDetailChange(planKey, "overpayment_amount", "");
                    }
                  }}
                />
                Pay Off Entire Loan
                <span
                  className="info-btn"
                  tabIndex={0}
                  aria-label="How pay off entire loan works"
                  onClick={handleInfoButtonInteraction}
                  onTouchStart={handleInfoButtonInteraction}
                >
                  <span className="info-icon">i</span>
                  <span className="info-tooltip">
                    Pay off the full loan remaining loan balance after the first month's interest is added and the first month's normal repayment is deducted
                  </span>
                </span>
              </label>
            ) : null}
          </div>
        </>
      )}
    </div>
  ));
}

export default PlanInputsSection;
